import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LAST_UPDATED = "November 24, 2025";

const termsDefinitions = [
  {
    term: "Provider",
    description:
      "A healthcare professional or organization using Anthro Care to manage or monitor patients.",
  },
  {
    term: "Designee",
    description:
      "A staff member, assistant, or third party authorized by a Provider to use the platform.",
  },
  {
    term: "Patient",
    description: "An individual receiving care services through the platform.",
  },
  {
    term: "PHI",
    description: "Protected Health Information as defined under HIPAA.",
  },
  {
    term: "Platform",
    description:
      "Anthro Care's web portal, app, monitoring tools, communication tools, surveys, alerts, and analytics systems.",
  },
];

const termsSections = [
  {
    id: "introduction",
    number: "1.",
    title: "Introduction",
    body: [
      `Welcome to Anthro Care, a Private Limited Company ("Anthro Care," "we," "us," or "our"). These Terms & Conditions ("Terms") govern your access to and use of our website, platform, mobile applications, and all related healthcare technology services (collectively, the "Services").`,
      "By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must not use the Services.",
    ],
  },
  {
    id: "scope",
    number: "2.",
    title: "Scope of Services",
    body: ["Anthro Care provides a secure healthcare technology platform that may include:"],
    bullets: [
      "Remote patient monitoring (RPM)",
      "Health assessments, surveys, and check-ins",
      "Monitoring through smart devices",
      "Messaging, reminders, and automated alerts",
      "Data analytics and insights",
      "Uploading medical photos/videos (e.g., wound images)",
      "Provider-patient communication tools",
      "Care coordination features",
    ],
    footer:
      "These Services are intended to support — not replace — the professional judgment and treatment plan of licensed healthcare Providers.",
  },
  {
    id: "emergency",
    number: "3.",
    title: "Not Emergency Services",
    body: [
      "Anthro Care IS NOT an emergency service. If you are experiencing an emergency or urgent medical crisis, call your local emergency number immediately.",
      "The platform must never be relied upon for emergency decision-making, life-supporting critical alerts, or time-sensitive medical responses.",
    ],
  },
  {
    id: "medical",
    number: "4.",
    title: "No Medical Advice",
    body: [
      "Anthro Care does not provide medical advice, diagnose, or treat conditions, and does not replace professional clinical judgment.",
      "All medical decisions should be made by Providers.",
    ],
  },
  {
    id: "responsibilities",
    number: "5.",
    title: "User Responsibilities",
    body: [
      "Users agree to provide accurate information, maintain confidentiality of credentials, use the platform lawfully, and avoid misuse or security risks.",
    ],
  },
  {
    id: "law",
    number: "6.",
    title: "Governing Law",
    body: [
      "These Terms are governed by the laws of the State of Wyoming, USA. Any disputes must be brought before courts in Wyoming.",
    ],
  },
  {
    id: "contact",
    number: "7.",
    title: "Contact Us",
    body: [
      "If you have questions about these Terms, contact:",
      "Anthro Care\n30 N Gould St Ste 100\nSheridan, WY 82801, USA\n📧 support@anthro.care",
    ],
  },
];

export default function TermsConditionsDialog({open ,setOpen, agreeTerms} :any) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleAccept = () => {
    if (agreedToTerms) {
      console.log("User accepted terms");
      agreeTerms();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} >
      <DialogContent showCloseButton={false} className="md:max-w-[95dvw] max-h-[95dvh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Terms & Conditions
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">Last Updated: {LAST_UPDATED}</p>
        </DialogHeader>

        <Tabs defaultValue="terms" className="w-full">
          {/* <TabsList className="w-full grid grid-cols-1">
            <TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
          </TabsList> */}

          <TabsContent value="terms" className="mt-4">
            <ScrollArea className="h-[55vh] pr-4">
              <div className="space-y-6">
                {/* Definitions */}
                <section className="space-y-3">
                  <h3 className="text-lg font-semibold">Definitions (Key Terms)</h3>
                  <div className="grid gap-3 rounded-lg border p-4 bg-gray-50">
                    {termsDefinitions.map((item) => (
                      <div key={item.term} className="space-y-1">
                        <p className="text-sm font-semibold text-gray-900">{item.term}</p>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <Separator />

                {/* Terms Sections */}
                {termsSections.map((section) => (
                  <section key={section.id} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-blue-600">
                        {section.number}
                      </span>
                      <h4 className="text-base font-semibold text-gray-900">
                        {section.title}
                      </h4>
                    </div>

                    {section.body?.map((paragraph, idx) => (
                      <p key={idx} className="text-sm text-gray-600 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}

                    {section.bullets && (
                      <ul className="list-disc ml-6 text-sm text-gray-700 space-y-1">
                        {section.bullets.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {section.footer && (
                      <p className="text-sm font-medium text-gray-700 mt-2">
                        {section.footer}
                      </p>
                    )}
                  </section>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <Separator className="my-4" />

        {/* Checkbox */}
        <div className="flex items-start space-x-3">
          <Checkbox
            id="terms"
            checked={agreedToTerms}
            onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
            className="mt-1"
          />
          <label htmlFor="terms" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
            I have read and agree to the{" "}
            <span className="font-semibold text-blue-600">Terms & Conditions</span>
          </label>
        </div>

        <DialogFooter className="flex gap-2">
          {/* <Button variant="outline" onClick={() => setOpen(false)}>
            Decline
          </Button> */}

          <Button
            onClick={handleAccept}
            disabled={!agreedToTerms}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
